import {
  Euler,
  Group,
  MathUtils,
  Mesh,
  Object3D,
  Quaternion,
  Vector2,
  Vector3
} from "three";
import { addBasicObjects } from "./addBasicObjects";
import { createAmbientFishLayer } from "./AmbientFish";
import { SceneProps } from "./SceneProps";
import { animate } from "@/lib/animate";
import { startWeaponUseSound, stopWeaponUseSound } from "@/lib/game-audio";
import { useSceneStore } from "@/lib/sceneStore";
import { SkimmerModel } from "./SkimmerModel";
import { createWeaponIkDriver } from "./createWeaponIkDriver";
import { getSceneRevealStages } from "./SceneReveal";
import { getPollutionLevelFromCounts } from "./PollutionEffects";
import {
  disposeGarbageObject,
  setRandomSafeGarbagePosition,
  showGarbageCollectionFeedback,
  setupGarbageObject,
  updateFloatingGarbageObject
} from "./GarbageObjects";
import { createGarbageHintTracker } from "./GarbageHintTracker";
import { showSuctionVortex, createPipeTravelGlow } from "./SuctionVortex";

const SUCTION_ANIMATION_DURATION = 1480;
const SUCTION_NEXT_ITEM_DELAY_MS = 500;
const SUCTION_ENTRY_PROGRESS = 0.66;
const SUCTION_FUNNEL_ROLL_START = 0.28;
const SUCTION_FUNNEL_MOUTH_OFFSET = 0.095;
const SUCTION_FUNNEL_ROLL_RADIUS = 0.032;
const SUCTION_FUNNEL_ROLL_TURNS = 2.4;
const SUCTION_ENTRY_SCALE = 0.05;
const SUCTION_PIPE_ENTRY_SCALE = 0.028;
const SUCTION_EXIT_SCALE = 0.015;
const SUCTION_HIT_RADIUS = 0.26;
const SUCTION_PIPE_HIT_RADIUS = 0.18;
const SUCTION_POINTER_RADIUS = 0.22;
const SUCTION_POINTER_TIP_MAX_DISTANCE = 0.42;
const HUD_RENDER_LAYER = 1;

const easeInOutCubic = (progress: number) =>
  progress < 0.5
    ? 4 * progress * progress * progress
    : 1 - Math.pow(-2 * progress + 2, 3) / 2;

const getPointToSegmentDistance = (
  point: Vector2,
  start: Vector2,
  end: Vector2
) => {
  const segmentX = end.x - start.x;
  const segmentY = end.y - start.y;
  const lengthSq = segmentX * segmentX + segmentY * segmentY;

  if (lengthSq <= 0.000001) {
    return point.distanceTo(start);
  }

  const progress = MathUtils.clamp(
    ((point.x - start.x) * segmentX + (point.y - start.y) * segmentY) /
      lengthSq,
    0,
    1
  );

  return Math.hypot(
    point.x - (start.x + segmentX * progress),
    point.y - (start.y + segmentY * progress)
  );
};

/** Локальный сдвиг оружия относительно инерционной HUD-группы. */
const WEAPON_VIEW_OFFSET = { x: 0, y: 0.1, z: -0.4 };

export const startBelugaScene = (props: SceneProps) => {
  const {
    scene,
    renderer,
    camera,
    canvas,
    models,
    onWin,
    weaponModel,
    garbageModels
  } = props;

  const { totalGarbageCount: totalCount } = useSceneStore.getState();

  const roomSize = 5;
  const baseCameraHeight = 2;
  const sceneStartedAt = performance.now();
  const getAmbientRevealProgress = () =>
    getSceneRevealStages(props.sceneReveal?.getProgress() ?? 1).decor;
  const getAmbientPollutionLevel = () => {
    const { collectedGarbageCount, totalGarbageCount } =
      useSceneStore.getState();
    return getPollutionLevelFromCounts(collectedGarbageCount, totalGarbageCount);
  };

  const { onExit, onUpdate, hudGroup, cameraHudGroup } = addBasicObjects(props);
  const ambientFish = createAmbientFishLayer(props, {
    roomSize,
    baseCameraHeight,
  });

  cameraHudGroup.traverse((child) => {
    child.layers.set(HUD_RENDER_LAYER);
  });
  camera.layers.enable(HUD_RENDER_LAYER);

  const stick = models[weaponModel.key].getModel();

  if (!stick) return { onExit: () => {}, onUpdate: () => {} };

  stick.frustumCulled = false;

  stick.layers.set(HUD_RENDER_LAYER);

  stick.rotateX(0.6);

  stick.scale.set(0.09, 0.09, 0.09)

  stick.traverse((child) => {
    child.layers.set(HUD_RENDER_LAYER);
    child.frustumCulled = false;

    if (child instanceof Mesh) child.renderOrder = 0;
  });

  stick.position.set(
    WEAPON_VIEW_OFFSET.x,
    WEAPON_VIEW_OFFSET.y,
    WEAPON_VIEW_OFFSET.z
  );

  hudGroup.add(stick);

  const playCleanerAnim = true;

  let cleanerAnim: {
    update: (dt: number) => void;
    dispose: () => void;
  } | null = null;
  
  if (playCleanerAnim && weaponModel instanceof SkimmerModel) {
    cleanerAnim = weaponModel.createHudAnimationDriver(stick);
  }

  const weaponIk = createWeaponIkDriver({
    root: stick,
    targetSpace: hudGroup,
    chain: [
      "Bone004",
      "Bone005",
      "Bone006",
      "Bone007",
      "Bone008",
      "Bone009",
      "Bone010",
      "Bone011",
      "Bone013",
      "Bone012"
    ],
    effector: "Bone012",
    horizontal: { x: 0.22, y: 0, z: 0 },
    vertical: { x: 0, y: 0.22, z: 0 },
    bias: { x: 0, y: 0.02, z: 0 },
    iterations: 8,
    maxAngle: 0.1,
    bendPlaneNormal: { x: 0, y: 0, z: 1 },
    bendPlaneSpace: stick,
    minReachRatio: 0.58,
    maxReachRatio: 0.96,
    maxJointSwing: MathUtils.degToRad(45)
  });

  let lastAnimT = performance.now();
  let lastGarbageUpdateTime = performance.now();

  const HOSE_BONE_NAMES = [
    "Bone004", "Bone005", "Bone006", "Bone007", "Bone008",
    "Bone009", "Bone010", "Bone011", "Bone013", "Bone012"
  ];
  const hoseBones = HOSE_BONE_NAMES
    .map(n => stick.getObjectByName(n))
    .filter((b): b is Object3D => b !== null);
  const hoseRestQuaternions = hoseBones.map((bone) => bone.quaternion.clone());
  const hoseWaveEuler = new Euler();
  const hoseWaveQuaternion = new Quaternion();
  const hoseTargetQuaternion = new Quaternion();
  let waveAmp = 0.0;

  const objects = new Set<Object3D>();
  const garbageHintTracker = createGarbageHintTracker(objects, camera);
  const occupiedGarbagePositions: Vector3[] = [];
  const objectWorldPosition = new Vector3();
  const projectedCoords = new Vector3();
  const projectedEffectorCoords = new Vector3();
  const projectedHoseStart = new Vector3();
  const projectedHoseEnd = new Vector3();
  const projectedEffector2d = new Vector2();
  const projectedObject2d = new Vector2();
  const targetWorldPosition = new Vector3();
  const targetLocalPosition = new Vector3();
  const effectorScenePosition = new Vector3();
  const hoseBoneWorldPosition = new Vector3();
  const fallbackTarget = stick.getObjectByName('Bone012');
  const projectedHoseSegments2d = HOSE_BONE_NAMES.slice(1).map(() => ({
    start: new Vector2(),
    end: new Vector2()
  }));

  const goods = garbageModels.map((m) => models[m.key]);

  for (let i = 0; i <= totalCount - 1; i++) {
    const model = goods[i % goods.length];
    const object = model.getModel();
    if (!object) continue;

    setRandomSafeGarbagePosition(object, roomSize, scene, camera, {
      index: i,
      count: totalCount,
      occupiedPositions: occupiedGarbagePositions,
      garbageKey: model.key
    });
    object.rotation.set(
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2
    );
    setupGarbageObject(object, model.key);
    objects.add(object);
    occupiedGarbagePositions.push(object.position.clone());
    scene.add(object);
  }

  const mousePos = new Vector2();
  const pointerTarget = new Vector2();
  const suctionHoldPrevWorldPos = new Vector3();
  const previousTouchAction = canvas.style.touchAction;
  let activePointerId: number | null = null;
  let activeSuctionVortexCleanup: (() => void) | null = null;
  let heldVacuumFxActive = false;
  let nextSuctionAllowedAt = 0;

  canvas.style.touchAction = "none";

  const updatePointerTarget = (e: PointerEvent) => {
    const { left, top, width, height } = canvas.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 2 - 1;
    const y = -(((e.clientY - top) / height) * 2 - 1);

    mousePos.set(
      MathUtils.clamp(x, -1, 1),
      MathUtils.clamp(y, -1, 1)
    );

    pointerTarget.copy(mousePos);
  }

  const onPointerDown = (e: PointerEvent) => {
    if (!e.isPrimary) return;

    activePointerId = e.pointerId;
    canvas.setPointerCapture(e.pointerId);
    e.preventDefault();
    updatePointerTarget(e);
  };

  const onPointerMove = (e: PointerEvent) => {
    if (activePointerId !== null && e.pointerId !== activePointerId) return;
    if (activePointerId === null && e.pointerType !== "mouse") return;

    e.preventDefault();
    updatePointerTarget(e);
  };

  const onPointerEnd = (e: PointerEvent) => {
    if (activePointerId !== e.pointerId) return;

    if (canvas.hasPointerCapture(e.pointerId)) {
      canvas.releasePointerCapture(e.pointerId);
    }

    activePointerId = null;
    pointerTarget.set(0, 0);
    updateHeldVacuumFx(false);
  };

  const getHoldSuctionTipTransform = (pos: Vector3, dir: Vector3) => {
    const tipBone = hoseBones[hoseBones.length - 1];

    if (tipBone) {
      tipBone.getWorldPosition(pos);
      const prevBone = hoseBones[hoseBones.length - 2];
      if (prevBone) {
        prevBone.getWorldPosition(suctionHoldPrevWorldPos);
        dir.subVectors(pos, suctionHoldPrevWorldPos).normalize();
      } else {
        dir.set(0, -1, 0);
      }
      return;
    }

    if (weaponIk.isReady) {
      weaponIk.getEffectorWorldPosition(pos);
    } else {
      fallbackTarget?.getWorldPosition(pos);
    }
    dir.set(0, -1, 0);
  };

  const updateHeldVacuumFx = (active: boolean) => {
    if (heldVacuumFxActive === active) return;
    heldVacuumFxActive = active;

    if (!active) {
      activeSuctionVortexCleanup?.();
      activeSuctionVortexCleanup = null;
      return;
    }

    if (activeSuctionVortexCleanup) return;

    activeSuctionVortexCleanup = showSuctionVortex(
      scene,
      getHoldSuctionTipTransform,
      1,
      {
        scale: 0.72,
        sustain: true,
        swirlStrength: 0.42,
      },
    );
  };

  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerup', onPointerEnd);
  canvas.addEventListener('pointercancel', onPointerEnd);
  canvas.addEventListener('lostpointercapture', onPointerEnd);

  return {
    onExit: () => {
      cleanerAnim?.dispose();
      ambientFish.dispose();
      objects.forEach(disposeGarbageObject);
      objects.clear();
      updateHeldVacuumFx(false);
      stopWeaponUseSound("beluga");
      canvas.style.touchAction = previousTouchAction;
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerEnd);
      canvas.removeEventListener('pointercancel', onPointerEnd);
      canvas.removeEventListener('lostpointercapture', onPointerEnd);
      garbageHintTracker.dispose();
      onExit();
    },
    onUpdate: () => {
      onUpdate();
      const frameNow = performance.now();
      const garbageDeltaSeconds = Math.min(
        (frameNow - lastGarbageUpdateTime) / 1000,
        0.05
      );
      lastGarbageUpdateTime = frameNow;
      const swayT = (frameNow - sceneStartedAt) / 1000;

      ambientFish.update(swayT, getAmbientRevealProgress(), getAmbientPollutionLevel());

      if (cleanerAnim) {
        const now = performance.now();
        cleanerAnim.update((now - lastAnimT) / 1000);
        lastAnimT = now;
      }

      const isPointing = activePointerId !== null;
      const isGamePaused = useSceneStore.getState().isGamePaused;
      updateHeldVacuumFx(isPointing && !isGamePaused);

      // Плавный fade: медленно уходит при поднятии пальца, медленно возвращается
      waveAmp += ((isPointing ? 0 : 1) - waveAmp) * 0.025;

      hoseBones.forEach((bone, i) => {
        const t = i / Math.max(hoseBones.length - 1, 1);
        const phase = t * Math.PI;
        const amp = (0.04 + t * 0.22) * waveAmp;

        hoseWaveEuler.set(
          Math.sin(swayT * 0.27 + phase) * amp,
          0,
          Math.sin(swayT * 0.20 + phase + 1.4) * amp * 0.05
        );
        hoseWaveQuaternion.setFromEuler(hoseWaveEuler);
        hoseTargetQuaternion
          .copy(hoseRestQuaternions[i])
          .multiply(hoseWaveQuaternion);
        // Slerp от текущей позиции к цели — плавный переход из IK в idle и обратно
        bone.quaternion.slerp(hoseTargetQuaternion, 0.08);
      });

      // IK работает только когда пользователь касается экрана.
      // В покое шланг держит волновая анимация — без IK-перегибов.
      if (isPointing) {
        weaponIk.setPointerTarget(pointerTarget);
        weaponIk.update();
      }

      const hasEffectorTarget = weaponIk.isReady || Boolean(fallbackTarget);
      const canUseEffector = isPointing && hasEffectorTarget;

      if (canUseEffector) {
        if (weaponIk.isReady) {
          weaponIk.getEffectorWorldPosition(targetWorldPosition);
        } else {
          fallbackTarget?.getWorldPosition(targetWorldPosition);
        }

        projectedEffectorCoords.copy(targetWorldPosition).project(camera);
        projectedEffector2d.set(projectedEffectorCoords.x, projectedEffectorCoords.y);
        effectorScenePosition.copy(targetWorldPosition);
        if (scene instanceof Group) scene.worldToLocal(effectorScenePosition);
      }

      const canCheckSuctionHit =
        canUseEffector &&
        !isGamePaused &&
        frameNow >= nextSuctionAllowedAt;

      let pointerCanReachObject = false;

      if (canCheckSuctionHit) {
        pointerCanReachObject =
          pointerTarget.distanceTo(projectedEffector2d) <
          SUCTION_POINTER_TIP_MAX_DISTANCE;

        for (let index = 1; index < hoseBones.length; index += 1) {
          hoseBones[index - 1].getWorldPosition(hoseBoneWorldPosition);
          projectedHoseStart.copy(hoseBoneWorldPosition).project(camera);
          hoseBones[index].getWorldPosition(hoseBoneWorldPosition);
          projectedHoseEnd.copy(hoseBoneWorldPosition).project(camera);

          const segment = projectedHoseSegments2d[index - 1];
          segment.start.set(projectedHoseStart.x, projectedHoseStart.y);
          segment.end.set(projectedHoseEnd.x, projectedHoseEnd.y);
        }
      }

      objects.forEach((object) => {
        if (object.userData.hasHit) return;

        updateFloatingGarbageObject(object, roomSize, baseCameraHeight, scene, camera, {
          attractorScenePosition: canUseEffector ? effectorScenePosition : null,
          deltaSeconds: garbageDeltaSeconds,
        });

        if (frameNow < nextSuctionAllowedAt) return;
        if (!canCheckSuctionHit) return;

        // Мусор живёт в gameGroup, а project(camera) ждёт world space.
        // После 3DoF-сдвига пола локальная object.position больше не совпадает
        // с реальной точкой на экране, поэтому проецируем world-позицию.
        object.getWorldPosition(objectWorldPosition);
        projectedCoords.copy(objectWorldPosition).project(camera);
        projectedObject2d.set(projectedCoords.x, projectedCoords.y);
        const distanceToEffector = projectedEffectorCoords.distanceTo(projectedCoords);
        const distanceToPointer = mousePos.distanceTo(projectedObject2d);
        let distanceToPipe = Number.POSITIVE_INFINITY;

        for (let index = 0; index < projectedHoseSegments2d.length; index += 1) {
          const segment = projectedHoseSegments2d[index];
          distanceToPipe = Math.min(
            distanceToPipe,
            getPointToSegmentDistance(
              projectedObject2d,
              segment.start,
              segment.end
            )
          );
        }

        if (
          (distanceToEffector < SUCTION_HIT_RADIUS ||
            distanceToPipe < SUCTION_PIPE_HIT_RADIUS ||
            (pointerCanReachObject && distanceToPointer < SUCTION_POINTER_RADIUS))
        ) {
          if (object.userData.hasHit) return;

          const hit = object;
          object.userData.hasHit = true;
          nextSuctionAllowedAt = frameNow + SUCTION_NEXT_ITEM_DELAY_MS;
          navigator.vibrate?.(40);

          if (hit) {
            const root = object
            if (root) {
              const skipRemoval = false;
    
              if (!skipRemoval) {
                const {
                  setCollectedGarbageCount,
                  collectedGarbageCount,
                  totalGarbageCount
                } = useSceneStore.getState();
    
                setCollectedGarbageCount(collectedGarbageCount + 1);
    
                if (collectedGarbageCount + 1 >= totalGarbageCount) {
                  onWin();
                }

                showGarbageCollectionFeedback(scene, root);

                if (hasEffectorTarget) {
                  targetLocalPosition.copy(targetWorldPosition);
                  // object.position локальная относительно gameGroup, поэтому
                  // world-точку носика пылесоса переводим в координаты родителя мусора.
                  object.parent?.worldToLocal(targetLocalPosition);
                  const startPos = object.position.clone();
                  const startScale = object.scale.clone();
                  const startRotation = object.rotation.clone();
                  const animatedTargetWorldPosition = targetWorldPosition.clone();
                  const animatedTargetLocalPosition = targetLocalPosition.clone();

                  const tipBone = hoseBones[hoseBones.length - 1];
                  const canUsePipePath = hoseBones.length >= 2;
                  const pipeWorldPos = new Vector3();
                  const pipeToPos = new Vector3();
                  const pipeLocalPos = new Vector3();
                  const suctionDir = new Vector3();
                  const suctionMouthWorldPos = new Vector3();
                  const suctionRollWorldPos = new Vector3();
                  const suctionSideA = new Vector3();
                  const suctionSideB = new Vector3();
                  const suctionBasis = new Vector3();
                  const suctionPrevWorldPos = new Vector3();

                  const getSuctionTipTransform = (pos: Vector3, dir: Vector3) => {
                    if (tipBone) {
                      tipBone.getWorldPosition(pos);
                      const prevBone = hoseBones[hoseBones.length - 2];
                      if (prevBone) {
                        prevBone.getWorldPosition(suctionPrevWorldPos);
                        dir.subVectors(pos, suctionPrevWorldPos).normalize();
                      } else {
                        dir.set(0, -1, 0);
                      }
                    } else if (weaponIk.isReady) {
                      weaponIk.getEffectorWorldPosition(pos);
                      dir.set(0, -1, 0);
                    } else {
                      fallbackTarget?.getWorldPosition(pos);
                      dir.set(0, -1, 0);
                    }
                  };

                  showSuctionVortex(
                    scene,
                    getSuctionTipTransform,
                    SUCTION_ANIMATION_DURATION,
                    {
                      scale: 0.78,
                      swirlStrength: 0.48,
                    },
                  );

                  const pipeGlow = createPipeTravelGlow(scene);
                  startWeaponUseSound("beluga");

                  animate({
                    duration: SUCTION_ANIMATION_DURATION,
                    onUpdate: (progress) => {
                      const p = MathUtils.clamp(progress, 0, 1);

                      if (p < SUCTION_ENTRY_PROGRESS || !canUsePipePath) {
                        pipeGlow.hide();
                        const entryProgress = MathUtils.clamp(
                          canUsePipePath ? p / SUCTION_ENTRY_PROGRESS : p,
                          0,
                          1
                        );

                        getSuctionTipTransform(animatedTargetWorldPosition, suctionDir);
                        if (suctionDir.lengthSq() <= 0.0001) {
                          suctionDir.set(0, -1, 0);
                        }
                        suctionDir.normalize();
                        suctionMouthWorldPos
                          .copy(animatedTargetWorldPosition)
                          .addScaledVector(suctionDir, SUCTION_FUNNEL_MOUTH_OFFSET);

                        if (entryProgress < SUCTION_FUNNEL_ROLL_START) {
                          const approachProgress = easeInOutCubic(
                            entryProgress / SUCTION_FUNNEL_ROLL_START
                          );

                          animatedTargetLocalPosition.copy(suctionMouthWorldPos);
                          object.parent?.worldToLocal(animatedTargetLocalPosition);
                          object.position.lerpVectors(
                            startPos,
                            animatedTargetLocalPosition,
                            approachProgress
                          );
                          object.rotation.copy(startRotation);
                          object.scale
                            .copy(startScale)
                            .multiplyScalar(MathUtils.lerp(1, 0.42, approachProgress));

                          return;
                        }

                        const rollProgress = MathUtils.clamp(
                          (entryProgress - SUCTION_FUNNEL_ROLL_START) /
                            (1 - SUCTION_FUNNEL_ROLL_START),
                          0,
                          1
                        );
                        const easedRollProgress = easeInOutCubic(rollProgress);
                        const rollAngle =
                          rollProgress * Math.PI * 2 * SUCTION_FUNNEL_ROLL_TURNS;
                        const rollRadius = MathUtils.lerp(
                          SUCTION_FUNNEL_ROLL_RADIUS,
                          0.004,
                          easedRollProgress
                        );

                        suctionBasis.set(
                          Math.abs(suctionDir.y) > 0.82 ? 1 : 0,
                          Math.abs(suctionDir.y) > 0.82 ? 0 : 1,
                          0
                        );
                        suctionSideA.crossVectors(suctionDir, suctionBasis).normalize();
                        suctionSideB.crossVectors(suctionDir, suctionSideA).normalize();

                        suctionRollWorldPos.lerpVectors(
                          suctionMouthWorldPos,
                          animatedTargetWorldPosition,
                          easedRollProgress
                        );
                        suctionRollWorldPos
                          .addScaledVector(suctionSideA, Math.cos(rollAngle) * rollRadius)
                          .addScaledVector(suctionSideB, Math.sin(rollAngle) * rollRadius);

                        animatedTargetLocalPosition.copy(suctionRollWorldPos);
                        object.parent?.worldToLocal(animatedTargetLocalPosition);
                        object.position.copy(animatedTargetLocalPosition);
                        object.rotation.set(
                          startRotation.x + easedRollProgress * Math.PI * 0.9,
                          startRotation.y + easedRollProgress * Math.PI * 1.8,
                          startRotation.z + easedRollProgress * Math.PI * 2.2
                        );
                        object.scale
                          .copy(startScale)
                          .multiplyScalar(
                            MathUtils.lerp(
                              0.42,
                              canUsePipePath ? SUCTION_ENTRY_SCALE : 0,
                              easedRollProgress
                            )
                          );
                      } else {
                        // Фаза 2: путешествие по костям шланга от кончика к основанию
                        const t = (p - SUCTION_ENTRY_PROGRESS) / (1 - SUCTION_ENTRY_PROGRESS);
                        const boneCount = hoseBones.length;
                        const scaledT = t * (boneCount - 1);
                        const idx = Math.min(Math.floor(scaledT), boneCount - 2);
                        const frac = scaledT - idx;
                        // hoseBones идут от основания к кончику, едем в обратную сторону
                        const fromBone = hoseBones[boneCount - 1 - idx];
                        const toBone = hoseBones[boneCount - 2 - idx];
                        fromBone.getWorldPosition(pipeWorldPos);
                        toBone.getWorldPosition(pipeToPos);
                        pipeWorldPos.lerp(pipeToPos, frac);
                        pipeLocalPos.copy(pipeWorldPos);
                        object.parent?.worldToLocal(pipeLocalPos);
                        object.position.copy(pipeLocalPos);
                        object.rotation.copy(startRotation);
                        object.scale
                          .copy(startScale)
                          .multiplyScalar(
                            MathUtils.lerp(
                              SUCTION_PIPE_ENTRY_SCALE,
                              SUCTION_EXIT_SCALE,
                              t
                            )
                          );

                        pipeGlow.update(pipeWorldPos, t);
                      }
                    },
                    onComplete: () => {
                      objects.delete(root);
                      scene.remove(root);
                      disposeGarbageObject(root);
                      pipeGlow.dispose();
                    }
                  });
                }
              }
            }
          }
        }
      });

      garbageHintTracker.update(swayT);
    },
    onRender: () => {
      const previousMask = camera.layers.mask;
      const previousAutoClear = renderer.autoClear;
      camera.layers.set(0);
      renderer.render(scene, camera);
      renderer.clearDepth();
      renderer.autoClear = false;
      camera.layers.set(HUD_RENDER_LAYER);
      renderer.render(scene, camera);
      renderer.autoClear = previousAutoClear;
      camera.layers.mask = previousMask;
    },
  };
};
